import { useState, useEffect } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { ArrowLeft, Clock, Calendar, Info } from 'lucide-react';
import { useStore } from '@/store/useStore';
import { t, getLocalizedString } from '@/i18n/translations';
import { HabitTemplate } from '@/types';
import LanguageSwitcher from '@/components/LanguageSwitcher';

const CATEGORIES = [
  { id: 'fitness', icon: '💪', key: 'fitness' as const },
  { id: 'sleep', icon: '😴', key: 'sleep' as const },
  { id: 'mindfulness', icon: '🧘', key: 'mindfulness' as const },
  { id: 'productivity', icon: '⚡', key: 'productivity' as const },
  { id: 'nutrition', icon: '🥗', key: 'nutrition' as const },
  { id: 'social', icon: '👥', key: 'social' as const },
  { id: 'learning', icon: '📚', key: 'learning' as const },
  { id: 'money', icon: '💰', key: 'money' as const },
  { id: 'cyber', icon: '🔒', key: 'cyber' as const },
];

const DAY_ORDER = ['ma', 'di', 'wo', 'do', 'vr', 'za', 'zo'];

export default function OnboardingPage() {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const { settings, progress, templates, addTemplate, setOnboardingComplete } = useStore();
  const lang = settings.language;

  const [step, setStep] = useState(1);
  const [selectedCategory, setSelectedCategory] = useState<string | null>(
    searchParams.get('category')
  );
  const [selectedTemplateId, setSelectedTemplateId] = useState<string | null>(null);

  // If we came with a category param, start at step 2
  useEffect(() => {
    if (searchParams.get('category')) {
      setStep(2);
    }
  }, [searchParams]);

  const categoryTemplates = templates.filter((t) => t.category === selectedCategory);
  const selectedTemplate = templates.find((t) => t.id === selectedTemplateId);

  const handleBack = () => {
    if (step === 1) {
      navigate('/');
    } else if (step === 2) {
      setSelectedCategory(null);
      setStep(1);
    } else if (step === 3) {
      setSelectedTemplateId(null);
      setStep(2);
    }
  };

  const handleCategorySelect = (categoryId: string) => {
    setSelectedCategory(categoryId);
    setStep(2);
  };

  const handleTemplateSelect = (templateId: string) => {
    setSelectedTemplateId(templateId);
    setStep(3);
  };

  const handleStart = () => {
    if (selectedTemplateId) {
      addTemplate(selectedTemplateId);
      setOnboardingComplete(true);
      navigate('/coach');
    }
  };

  return (
    <div className="min-h-screen flex flex-col pb-20">
      {/* Header */}
      <header className="flex items-center justify-between px-4 py-3 bg-white/80 backdrop-blur-sm">
        <button
          onClick={handleBack}
          className="flex items-center gap-2 text-gray-600 hover:text-gray-900"
        >
          <ArrowLeft className="w-5 h-5" />
          <span className="text-sm font-medium">{t('onboarding.back', lang)}</span>
        </button>
        <span className="text-sm text-gray-500">
          {t('onboarding.step', lang, { current: step, total: 3 })}
        </span>
        <LanguageSwitcher />
      </header>

      {/* Content */}
      <main className="flex-1 px-4 py-6">
        {step === 1 && (
          <CategoryStep
            lang={lang}
            onSelect={handleCategorySelect}
          />
        )}

        {step === 2 && selectedCategory && (
          <TemplateStep
            lang={lang}
            category={selectedCategory}
            templates={categoryTemplates}
            onSelect={handleTemplateSelect}
          />
        )}

        {step === 3 && selectedTemplate && (
          <ScheduleStep
            lang={lang}
            template={selectedTemplate}
            onStart={handleStart}
          />
        )}
      </main>
    </div>
  );
}

function CategoryStep({
  lang,
  onSelect,
}: {
  lang: 'nl' | 'fr' | 'en';
  onSelect: (id: string) => void;
}) {
  return (
    <div className="max-w-md mx-auto">
      <h1 className="text-2xl font-bold text-gray-900 text-center mb-2">
        {t('onboarding.chooseCategory', lang)}
      </h1>
      <p className="text-sm text-gray-500 text-center mb-6">
        {t('onboarding.hint', lang)}
      </p>

      <div className="space-y-3">
        {CATEGORIES.map((cat) => (
          <button
            key={cat.id}
            onClick={() => onSelect(cat.id)}
            className="w-full bg-white/80 backdrop-blur-sm rounded-xl p-4 shadow-sm hover:shadow-md transition-all flex items-center gap-4 text-left"
          >
            <span className="text-3xl">{cat.icon}</span>
            <div>
              <div className="font-semibold text-gray-900">
                {t(`category.${cat.key}`, lang)}
              </div>
              <div className="text-sm text-gray-600">
                {t(`category.${cat.key}.desc`, lang)}
              </div>
            </div>
          </button>
        ))}
      </div>
    </div>
  );
}

function TemplateStep({
  lang,
  category,
  templates,
  onSelect,
}: {
  lang: 'nl' | 'fr' | 'en';
  category: string;
  templates: HabitTemplate[];
  onSelect: (id: string) => void;
}) {
  const categoryInfo = CATEGORIES.find((c) => c.id === category);

  return (
    <div className="max-w-md mx-auto">
      <div className="flex items-center gap-2 mb-2">
        <span className="text-2xl">{categoryInfo?.icon}</span>
        <span className="font-medium text-gray-700">
          {categoryInfo && t(`category.${categoryInfo.key}`, lang)}
        </span>
      </div>

      <h1 className="text-2xl font-bold text-gray-900 mb-6">
        {t('onboarding.chooseTemplate', lang)}
      </h1>

      <div className="space-y-3">
        {templates.map((template) => (
          <button
            key={template.id}
            onClick={() => onSelect(template.id)}
            className="w-full bg-white/80 backdrop-blur-sm rounded-xl p-4 shadow-sm hover:shadow-md transition-all text-left"
          >
            <div className="flex items-center gap-3 mb-2">
              <span className="text-2xl">{template.icon}</span>
              <div className="font-semibold text-gray-900">
                {getLocalizedString(template.name, lang)}
              </div>
            </div>
            <p className="text-sm text-gray-600 mb-3">
              {getLocalizedString(template.description, lang)}
            </p>
            <div className="flex items-center gap-4 text-xs text-gray-500">
              <span className="flex items-center gap-1">
                <Clock className="w-3.5 h-3.5" />
                {getLocalizedString(template.weeklyTimeEstimate, lang)}
              </span>
              <span className="flex items-center gap-1">
                <Calendar className="w-3.5 h-3.5" />
                {template.durationDays} {lang === 'nl' ? 'dagen' : lang === 'fr' ? 'jours' : 'days'}
              </span>
            </div>
          </button>
        ))}
      </div>
    </div>
  );
}

function ScheduleStep({
  lang,
  template,
  onStart,
}: {
  lang: 'nl' | 'fr' | 'en';
  template: HabitTemplate;
  onStart: () => void;
}) {
  // Group habits by time slot for the calendar view
  const timeSlots = ['07:00', '08:00', '09:00', '10:00', '12:00', '18:00', '19:00', '20:00', '21:00'];

  // Create schedule grid: time -> day -> habits
  const scheduleByTimeAndDay: Record<string, Record<string, { icon: string; name: string; duration: string | null }[]>> = {};

  // Also track "all day" habits (no specific time)
  const allDayHabits: { icon: string; name: string; days: string[] }[] = [];

  template.habits.forEach((habit) => {
    const habitDays = habit.days || DAY_ORDER;
    if (!habit.targetTime) {
      // All day habit
      allDayHabits.push({
        icon: habit.icon,
        name: getLocalizedString(habit.name, lang),
        days: habitDays,
      });
    } else {
      // Find closest time slot
      const habitHour = parseInt(habit.targetTime.split(':')[0]);
      let closestSlot = timeSlots[0];
      let minDiff = 24;

      for (const slot of timeSlots) {
        const slotHour = parseInt(slot.split(':')[0]);
        const diff = Math.abs(slotHour - habitHour);
        if (diff < minDiff) {
          minDiff = diff;
          closestSlot = slot;
        }
      }

      if (!scheduleByTimeAndDay[closestSlot]) {
        scheduleByTimeAndDay[closestSlot] = {};
        DAY_ORDER.forEach((day) => {
          scheduleByTimeAndDay[closestSlot][day] = [];
        });
      }

      habitDays.forEach((day) => {
        scheduleByTimeAndDay[closestSlot][day].push({
          icon: habit.icon,
          name: getLocalizedString(habit.name, lang),
          duration: habit.duration,
        });
      });
    }
  });

  // Get only time slots that have habits
  const activeTimeSlots = timeSlots.filter((slot) => scheduleByTimeAndDay[slot]);

  return (
    <div className="max-w-md mx-auto">
      <div className="flex items-center gap-2 mb-2">
        <span className="text-2xl">{template.icon}</span>
        <span className="font-medium text-gray-700">
          {getLocalizedString(template.name, lang)}
        </span>
      </div>

      <h1 className="text-2xl font-bold text-gray-900 mb-4">
        {t('onboarding.yourSchedule', lang)}
      </h1>

      {/* Calendar View */}
      <div className="bg-white/80 backdrop-blur-sm rounded-xl shadow-sm mb-4 overflow-hidden">
        {/* Day headers */}
        <div className="grid grid-cols-8 border-b border-gray-200">
          <div className="p-2 text-xs font-medium text-gray-400 text-center"></div>
          {DAY_ORDER.map((day) => (
            <div key={day} className="p-2 text-xs font-medium text-gray-500 uppercase text-center border-l border-gray-100">
              {t(`day.${day}` as any, lang)}
            </div>
          ))}
        </div>

        {/* Time slots */}
        {activeTimeSlots.map((slot) => (
          <div key={slot} className="grid grid-cols-8 border-b border-gray-100 last:border-b-0">
            <div className="p-2 text-xs text-gray-400 text-right pr-3 bg-gray-50/50">
              {slot}
            </div>
            {DAY_ORDER.map((day) => {
              const habits = scheduleByTimeAndDay[slot]?.[day] || [];
              return (
                <div key={day} className="p-1 min-h-[40px] border-l border-gray-100 flex flex-col items-center justify-center gap-0.5">
                  {habits.map((habit, idx) => (
                    <div
                      key={idx}
                      className="text-base"
                      title={`${habit.name}${habit.duration ? ` (${habit.duration})` : ''}`}
                    >
                      {habit.icon}
                    </div>
                  ))}
                </div>
              );
            })}
          </div>
        ))}

        {/* All day habits */}
        {allDayHabits.length > 0 && (
          <div className="grid grid-cols-8 border-t border-gray-200 bg-gray-50/30">
            <div className="p-2 text-xs text-gray-400 text-right pr-3">
              {t('tracker.allDay', lang)}
            </div>
            {DAY_ORDER.map((day) => {
              const dayHabits = allDayHabits.filter((h) => h.days.includes(day));
              return (
                <div key={day} className="p-1 min-h-[40px] border-l border-gray-100 flex flex-col items-center justify-center gap-0.5">
                  {dayHabits.map((habit, idx) => (
                    <div key={idx} className="text-base" title={habit.name}>
                      {habit.icon}
                    </div>
                  ))}
                </div>
              );
            })}
          </div>
        )}
      </div>

      {/* Legend */}
      <div className="bg-white/60 backdrop-blur-sm rounded-xl p-3 mb-4">
        <div className="flex flex-wrap gap-x-4 gap-y-2 justify-center">
          {template.habits.map((habit) => (
            <div key={habit.id} className="flex items-center gap-1.5 text-xs text-gray-600">
              <span className="text-sm">{habit.icon}</span>
              <span>{getLocalizedString(habit.name, lang)}</span>
              {habit.duration && (
                <span className="text-gray-400">({habit.duration})</span>
              )}
            </div>
          ))}
        </div>
      </div>

      {/* Time estimate */}
      <p className="text-center text-gray-600 mb-4">
        {t('onboarding.totalTime', lang, { time: getLocalizedString(template.weeklyTimeEstimate, lang) })}
      </p>

      {/* Info box */}
      <div className="bg-blue-50 rounded-xl p-3 flex items-start gap-3 mb-6">
        <Info className="w-5 h-5 text-blue-500 flex-shrink-0 mt-0.5" />
        <p className="text-sm text-blue-700">
          {t('onboarding.adjustLater', lang)}
        </p>
      </div>

      {/* Start button */}
      <button
        onClick={onStart}
        className="w-full bg-primary-500 hover:bg-primary-600 text-white font-semibold py-3 rounded-full shadow-lg transition-all hover:shadow-xl"
      >
        {t('onboarding.start', lang)} →
      </button>
    </div>
  );
}
